import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SortIcon } from '@/components/common/SortIcon';
import { DeleteDialog } from '@/components/common/Dialogs';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { complaintsQueryKeys } from '../api';
import { CreateComplaintDialog } from './CreateComplaintDialog';
import type { Complaint, ComplaintFilters } from '../types';

interface GetColumnsParams {
  orderBy: string;
  order: 'asc' | 'desc' | '';
  onSort: (field: string) => void;
  filters: ComplaintFilters;
}

export function useComplaintColumns({
  orderBy,
  order,
  onSort,
}: GetColumnsParams): ColumnDef<Complaint>[] {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);

  const columns: ColumnDef<Complaint>[] = [
    {
      accessorKey: 'comment',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('comment')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('comment')}
        >
          <p>{t('comment')}</p>
          <SortIcon active={orderBy === 'comment'} direction={order} />
        </div>
      ),
      cell: ({ row }) => (
        <Link
          to={`/app/complaints/${row.original.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original.comment?.substring(0, 50)}
          {row.original.comment?.length > 50 ? '...' : ''}
        </Link>
      ),
    },
    {
      accessorKey: 'user',
      header: () => <p>{t('user')}</p>,
      cell: ({ row }) => (
        <Link
          to={`/app/users/${row.original.user?.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original.user?.name || '-'}
        </Link>
      ),
    },
    {
      accessorKey: 'client',
      header: () => <p>{t('client')}</p>,
      cell: ({ row }) => (
        <Link
          to={`/app/clients/${row.original.client?.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original.client?.name || '-'}
        </Link>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer select-none"
          role="button"
          tabIndex={0}
          onClick={() => onSort('createdAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('createdAt')}
        >
          <p>{t('createdAt')}</p>
          <SortIcon active={orderBy === 'createdAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const dateStr = row.original.createdAt;
        return dateStr ? (
          <div>{format(new Date(dateStr), 'yyyy-MM-dd HH:mm')}</div>
        ) : (
          <span>-</span>
        );
      },
    },
  ];

  if (hasAccess('complaint', 'update') || hasAccess('complaint', 'delete')) {
    columns.splice(0, 0, {
      id: 'actions',
      header: t('actionsLabel'),
      cell: ({ row }) => {
        const data = row.original;
        return (
          <div className="flex">
            {hasAccess('complaint', 'update') && <CreateComplaintDialog id={data.id} />}
            {hasAccess('complaint', 'delete') && (
              <DeleteDialog
                id={data.id}
                title={data.comment?.substring(0, 30) || `Complaint #${data.id}`}
                url="complaints"
                queryKey={complaintsQueryKeys.lists()}
              />
            )}
          </div>
        );
      },
      enableHiding: false,
    });
  }

  return columns;
}
